import { get, post } from '../utils/FetchV2.util';

// ─── Tipos del contrato del backend (heru-fiscal-engine) ───────────────────
// Mantener en sync con internal/modules/fiscalvalidation/domain.go.

export type CurpExtractionStatus =
	| 'pending'
	| 'running'
	| 'done'
	| 'failed'
	| 'stale';

export type ExtractionSourceName = 'extractions-engine' | 'business-process';
export type ExtractionSourceStatus = 'fired' | 'failed';

export type ExtractionSource = {
	name: ExtractionSourceName;
	status: ExtractionSourceStatus;
	workflow_id?: string;
	run_id?: string;
	request_id?: string;
	message?: string;
	error?: string;
};

export type TriggerResult = {
	curp: string;
	sources: ExtractionSource[];
};

export type CurpExtractionSourceInfo = {
	present: boolean;
	state?: string | null;
	updated_at?: string | null;
};

export type CurpExtractionSourcesInfo = {
	extraction: CurpExtractionSourceInfo;
	validation: CurpExtractionSourceInfo;
};

// El backend marca casi todo como opcional/nullable porque la fila se llena
// progresivamente. El cliente revela cada campo conforme deja de ser null.
export type CurpExtractionState = {
	curp: string;
	status: CurpExtractionStatus;

	extraction_success?: boolean | null;
	extraction_error?: string | null;
	extracted_at?: string | null;

	nombre?: string | null;
	apellido_paterno?: string | null;
	apellido_materno?: string | null;
	nombre_completo?: string | null;
	birth_date?: string | null;
	gender?: string | null;
	curp_state_code?: string | null;
	estado_nacimiento?: string | null;

	rfc?: string | null;
	rfc_valid?: boolean | null;
	rfc_invoices_eligible?: boolean | null;
	rfc_message?: string | null;
	rfc_source?: string | null;
	regimes?: string[];
	sat_platforms?: string[];
	suspended?: boolean | null;
	postal_code?: string | null;
	estado_domicilio_fiscal?: string | null;

	csd_activo?: boolean | null;
	csd_vigencia_inicio?: string | null;
	csd_vigencia_fin?: string | null;
	efirma_activa?: boolean | null;
	efirma_vigencia_inicio?: string | null;
	efirma_vigencia_fin?: string | null;
	fiel_total?: number | null;
	certificados?: unknown;
	enrichment_data?: unknown;

	valid?: boolean | null;
	active?: boolean | null;
	fc3_eligible?: boolean | null;
	processing_message?: string | null;

	// Actividad económica: descripciones crudas del SAT de la última CSF del
	// RFC. Solo existen después de extraer la CSF (tras vincular la e.firma);
	// ausentes = aún no sabemos.
	activity_descs?: string[] | null;
	activities_extracted_at?: string | null;

	sources: CurpExtractionSourcesInfo;
};

export const CURP_EXTRACTION_TERMINAL_STATUSES: CurpExtractionStatus[] = [
	'done',
	'failed',
	'stale',
];

/** Llave de react-query del estado de extracción de una CURP. La comparten el
 * polling de CurpExtractionContext y la espera de «Generar mi RFC». */
export const curpExtractionQueryKey = (curp: string | null) =>
	['curp-extraction', curp] as const;

// ─── API calls ──────────────────────────────────────────────────────────────

// Dispara A + B en paralelo. Idempotente por (user_id, curp) durante 10min
// del lado del backend: dos POSTs consecutivos con el mismo CURP devuelven los
// mismos workflow_id/request_id sin generar workflows extra.
export const triggerCurpExtractionService = async (
	curp: string,
): Promise<TriggerResult> => {
	return post<TriggerResult>({
		service: 'fiscalEngine',
		endpoint: '/curp-extraction',
		body: { curp },
		skipAccountError: true,
	});
};

// Lee el estado mergeado. Polling-friendly: siempre 200 con `status` derivado.
export const getCurpExtractionService = async (
	curp: string,
): Promise<CurpExtractionState> => {
	return get<CurpExtractionState>({
		service: 'fiscalEngine',
		endpoint: `/curp-extraction?curp=${encodeURIComponent(curp)}`,
	});
};
